import { API_BASE_URL } from '@/config/constants'
import { getAuthToken } from '@/utils/auth'

/**
 * API Response wrapper from backend
 */
interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
  meta?: any
}

/**
 * Single exchange rate response
 */
export interface ExchangeRate {
  from: string
  to: string
  rate: number
  timestamp: string
  source: string
  currencyPair?: string
}

/**
 * Multiple exchange rates response
 */
export interface MultipleExchangeRates {
  base: string
  rates: ExchangeRate[]
  count: number
}

/**
 * Supported currencies response
 */
export interface SupportedCurrencies {
  currencies: string[]
  count: number
  note?: string
}

function throwLiveRateUnavailable(from: string, to: string, reason: string): never {
  throw new Error(`Live exchange rate unavailable for ${from}/${to}: ${reason}`)
}

/**
 * Get live exchange rate for a currency pair
 * @param from Base currency code (ISO 4217, e.g., 'USD')
 * @param to Target currency code (ISO 4217, e.g., 'ZAR')
 * @returns Exchange rate data (uses fallback when API returns 401/404)
 */
export async function getExchangeRate(
  from: string = 'USD',
  to: string = 'ZAR'
): Promise<ExchangeRate> {
  const token = getAuthToken()
  const url = `${API_BASE_URL}/exchange-rates/live?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(url, { method: 'GET', headers })

  if (response.status === 401) {
    throwLiveRateUnavailable(from, to, 'Unauthorized')
  }

  if (response.status === 404) {
    throwLiveRateUnavailable(from, to, 'Endpoint not found')
  }

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Failed to fetch exchange rate: ${response.statusText} - ${errorText}`)
  }

  const data: ApiResponse<ExchangeRate> = await response.json()
  if (!data.success) {
    throwLiveRateUnavailable(from, to, 'API returned unsuccessful response')
  }

  return data.data
}

/**
 * Get multiple exchange rates from a base currency
 * @param from Base currency code (ISO 4217, e.g., 'USD')
 * @param to Array of target currency codes (e.g., ['ZAR', 'EUR', 'GBP'])
 * @returns Multiple exchange rates
 */
export async function getMultipleExchangeRates(
  from: string,
  to: string[]
): Promise<MultipleExchangeRates> {
  const token = getAuthToken()
  if (!token) {
    throw new Error('Authentication token not found')
  }

  const toParam = to.join(',')
  const url = `${API_BASE_URL}/exchange-rates/multiple?from=${encodeURIComponent(from)}&to=${encodeURIComponent(toParam)}`
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Failed to fetch exchange rates: ${response.statusText} - ${errorText}`)
  }

  const data: ApiResponse<MultipleExchangeRates> = await response.json()
  
  if (!data.success) {
    throw new Error('Failed to fetch exchange rates')
  }

  return data.data
}

/**
 * Get all exchange rates from USD to all supported currencies
 * @returns All exchange rates from USD
 */
export async function getAllExchangeRates(): Promise<MultipleExchangeRates> {
  const token = getAuthToken()
  if (!token) {
    throw new Error('Authentication token not found')
  }

  const url = `${API_BASE_URL}/exchange-rates/all`
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Failed to fetch all exchange rates: ${response.statusText} - ${errorText}`)
  }

  const data: ApiResponse<MultipleExchangeRates> = await response.json()
  
  if (!data.success) {
    throw new Error('Failed to fetch all exchange rates')
  }

  return data.data
}

/**
 * Get list of supported currency codes
 * @returns List of supported currencies from the live API
 */
export async function getSupportedCurrencies(): Promise<string[]> {
  const token = getAuthToken()
  const url = `${API_BASE_URL}/exchange-rates/currencies`

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(url, { method: 'GET', headers })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Failed to fetch supported currencies: ${response.statusText} - ${errorText}`)
  }

  const data: ApiResponse<SupportedCurrencies> = await response.json()
  if (!data.success) {
    throw new Error('Failed to fetch supported currencies')
  }

  return data.data.currencies
}
